import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'
import { PWAThunk } from 'lib/core/data'
import { Callback } from 'lib/types'
import { setCart } from '../cart'
import { Slice, GuestEmail } from './types'

export const initialState: Slice = {}

interface ActionData {
  cartId: string
  uuid: string
}

const getSlice = (slice: GuestEmail, uuid?: string): GuestEmail => {
  return slice ?? ({
    uuid,
    isFetching: false,
    isSuccess: false,
    isFailure: false
  })
}

const slice = createSlice({
  name: 'quote/setGuestEmail',
  initialState,
  reducers: {
    request (state, action: PayloadAction<ActionData>) {
      const { cartId, uuid } = action.payload
      const slice = getSlice(state[cartId], uuid)
      slice.uuid = uuid
      slice.isFetching = true
      state[cartId] = slice
    },

    success (state, action: PayloadAction<ActionData>) {
      const { cartId, uuid } = action.payload
      const slice = getSlice(state[cartId])
      if (uuid === slice.uuid) {
        slice.isFetching = false
        slice.isSuccess = true
        slice.isFailure = false
      }
    },

    failure (state, action: PayloadAction<ActionData>) {
      const { cartId, uuid } = action.payload
      const slice = getSlice(state[cartId])
      if (uuid === slice.uuid) {
        slice.isFetching = false
        slice.isSuccess = false
        slice.isFailure = true
      }
    }
  }
})

export const reducer = slice.reducer

export const { request, success, failure } = slice.actions

export function setGuestEmail (cartId: string, email: string, callback?: Callback): PWAThunk {
  return async (dispatch, getState, params) => {
    const { magentoClient } = params
    const uuid = uuidv4()
    dispatch(request({ cartId, uuid }))
    dispatch(setCart({
      cartId,
      data: { email },
      merge: true
    }))

    try {
      const response = await magentoClient.getClient().quote.setGuestEmail({
        cartId,
        email
      })

      const { data } = response
      const { setGuestEmailOnCart } = data
      const { cart } = setGuestEmailOnCart

      if (cart.email === email) {
        dispatch(success({ cartId, uuid }))
        callback?.onSuccess?.()
      } else {
        dispatch(failure({ cartId, uuid }))
        callback?.onFailure?.({
          error: new Error(`[Guest Email] Unexpected payload: ${JSON.stringify(response)}`),
          extraData: { cartId, email }
        })
      }

      return response
    } catch (e) {
      dispatch(failure({ cartId, uuid }))
      callback?.onFailure?.({
        error: new Error(`[Guest Email] ${e.message}`),
        extraData: {
          cartId,
          email
        }
      })
    }
  }
}
